import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Upload, Loader2, CheckCircle2, Languages, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/context/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_BYTES = 8 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/png"];
const FREELANCE_LOGO = "https://static.prod-images.emergentagent.com/jobs/5f8901d4-b792-4831-8e65-f6410c5f303d/images/2867f08999dc43df8a8bc60a04abab015ee75de61beb1368ee2bc38fad7e6c7c.jpeg";

// Resize + compress to keep uploads small (well under proxy limits).
const compressImage = (file: File, maxDim = 1280, quality = 0.82): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        if (width >= height) { height = Math.round((height * maxDim) / width); width = maxDim; }
        else { width = Math.round((width * maxDim) / height); height = maxDim; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("no ctx"));
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("compress failed"))), "image/jpeg", quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("load failed")); };
    img.src = url;
  });

// Module-level so the hidden file input keeps a stable identity across renders.
const PhotoUpload = ({ which, preview, inputRef, onChange, labelText, hintText, changeText }: {
  which: 1 | 2;
  preview: string;
  inputRef: React.RefObject<HTMLInputElement>;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  labelText: string;
  hintText: string;
  changeText: string;
}) => (
  <div>
    <input
      ref={inputRef}
      type="file"
      accept="image/jpeg,image/png"
      className="hidden"
      data-testid={`freelance-photo${which}-input`}
      onChange={onChange}
    />
    {preview ? (
      <div className="relative rounded-xl overflow-hidden border-2 border-purple-200 aspect-square">
        <img src={preview} alt={`Photo ${which}`} className="w-full h-full object-cover" />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          data-testid={`freelance-photo${which}-change`}
          className="absolute bottom-2 right-2 bg-purple-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow hover:bg-purple-700"
        >
          {changeText}
        </button>
      </div>
    ) : (
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        data-testid={`freelance-photo${which}-btn`}
        className="w-full aspect-square rounded-xl border-2 border-dashed border-purple-300 bg-purple-50 hover:bg-purple-100 flex flex-col items-center justify-center gap-2 text-purple-700 transition-colors"
      >
        <Upload className="w-7 h-7" />
        <span className="text-sm font-semibold">{labelText}</span>
        <span className="text-[11px] text-purple-500">{hintText}</span>
      </button>
    )}
  </div>
);

const emptyForm = {
  displayName: "", firstName: "", lastName: "", location: "", workTelephone: "",
  workLineId: "", workWhatsapp: "", email: "", age: "", height: "",
};

const FreelancePage = () => {
  const { t, toggleLanguage, language } = useLanguage();
  const [form, setForm] = useState({ ...emptyForm });
  const [photo1, setPhoto1] = useState<Blob | null>(null);
  const [photo2, setPhoto2] = useState<Blob | null>(null);
  const [preview1, setPreview1] = useState<string>("");
  const [preview2, setPreview2] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const file1Ref = useRef<HTMLInputElement>(null);
  const file2Ref = useRef<HTMLInputElement>(null);

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const handlePhoto = async (which: 1 | 2, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED.includes(file.type)) { toast.error(t("freelance.errPhotoType")); return; }
    if (file.size > MAX_BYTES) { toast.error(t("freelance.errPhotoSize")); return; }
    try {
      const blob = await compressImage(file);
      const url = URL.createObjectURL(blob);
      if (which === 1) { setPhoto1(blob); setPreview1(url); }
      else { setPhoto2(blob); setPreview2(url); }
    } catch {
      toast.error(t("freelance.errSubmit"));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const required = ["displayName", "firstName", "lastName", "location", "workTelephone"] as const;
    if (required.some((k) => !form[k].trim())) { toast.error(t("freelance.errRequired")); return; }
    if (!form.workLineId.trim() && !form.workWhatsapp.trim()) { toast.error(t("freelance.errContact")); return; }
    if (!photo1 || !photo2) { toast.error(t("freelance.errPhotos")); return; }

    const fd = new FormData();
    fd.append("display_name", form.displayName.trim());
    fd.append("first_name", form.firstName.trim());
    fd.append("last_name", form.lastName.trim());
    fd.append("location", form.location.trim());
    fd.append("work_telephone", form.workTelephone.trim());
    fd.append("work_line_id", form.workLineId.trim());
    fd.append("work_whatsapp", form.workWhatsapp.trim());
    fd.append("email", form.email.trim());
    fd.append("age", form.age.trim());
    fd.append("height", form.height.trim());
    fd.append("photo1", photo1, "photo1.jpg");
    fd.append("photo2", photo2, "photo2.jpg");

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/freelancers`, { method: "POST", body: fd });
      if (res.ok) {
        setSubmitted(true);
      } else {
        const data = await res.json().catch(() => ({}));
        const msg = typeof data.detail === "string" ? data.detail : t("freelance.errSubmit");
        toast.error(msg);
      }
    } catch {
      toast.error(t("freelance.errSubmit"));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({ ...emptyForm });
    setPhoto1(null); setPhoto2(null); setPreview1(""); setPreview2("");
    setSubmitted(false);
  };

  const Req = () => <span className="text-rose-500">*</span>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white" data-testid="freelance-page">
      <header className="pt-6 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" data-testid="freelance-back-btn">
            <Button variant="ghost" size="sm" className="gap-2 text-purple-700 hover:text-purple-900 hover:bg-purple-100">
              <ArrowLeft className="w-4 h-4" />
              {t("home.backToHome")}
            </Button>
          </Link>
          <Button variant="outline" size="sm" onClick={toggleLanguage} className="gap-2 border-purple-300 text-purple-700" data-testid="freelance-lang-toggle">
            <Languages className="w-4 h-4" />
            {language === "en" ? "ไทย" : "EN"}
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 pb-20 pt-4">
        <div className="text-center mb-8">
          <div className="inline-block bg-white rounded-2xl shadow-sm px-8 py-5">
            <img src={FREELANCE_LOGO} alt="Angel Freelance" className="mx-auto w-full max-w-xs sm:max-w-sm h-auto object-contain" data-testid="freelance-logo" />
          </div>
          <h1 className="sr-only" data-testid="freelance-title">{t("freelance.title")}</h1>
          <p className="mt-4 text-base text-gray-600 max-w-2xl mx-auto">{t("freelance.intro")}</p>
        </div>

        {submitted ? (
          <Card className="max-w-lg mx-auto border-purple-200" data-testid="freelance-success">
            <CardContent className="py-12 text-center">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
              <h2 className="mt-4 text-2xl font-bold text-gray-800">{t("freelance.successTitle")}</h2>
              <p className="mt-2 text-gray-600">{t("freelance.successMsg")}</p>
              <Button className="mt-6 bg-purple-600 hover:bg-purple-700" onClick={resetForm} data-testid="freelance-submit-another">
                {t("freelance.submitAnother")}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-purple-200 shadow-sm">
            <CardContent className="p-5 sm:p-8">
              <h2 className="text-lg font-semibold text-gray-800 mb-5">{t("freelance.formTitle")}</h2>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="fl-display">{t("freelance.displayName")} <Req /></Label>
                  <Input id="fl-display" data-testid="freelance-display-name" value={form.displayName} onChange={(e) => set("displayName", e.target.value)} />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="fl-first">{t("freelance.firstName")} <Req /></Label>
                    <Input id="fl-first" data-testid="freelance-first-name" value={form.firstName} onChange={(e) => set("firstName", e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="fl-last">{t("freelance.lastName")} <Req /></Label>
                    <Input id="fl-last" data-testid="freelance-last-name" value={form.lastName} onChange={(e) => set("lastName", e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label htmlFor="fl-location">{t("freelance.location")} <Req /></Label>
                  <Input id="fl-location" data-testid="freelance-location" placeholder={t("freelance.locationPlaceholder")} value={form.location} onChange={(e) => set("location", e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="fl-tel">{t("freelance.workTelephone")} <Req /></Label>
                  <Input id="fl-tel" type="tel" data-testid="freelance-telephone" value={form.workTelephone} onChange={(e) => set("workTelephone", e.target.value)} />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="fl-line">{t("freelance.workLineId")}</Label>
                    <Input id="fl-line" data-testid="freelance-line-id" value={form.workLineId} onChange={(e) => set("workLineId", e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="fl-wa">{t("freelance.workWhatsapp")}</Label>
                    <Input id="fl-wa" data-testid="freelance-whatsapp" value={form.workWhatsapp} onChange={(e) => set("workWhatsapp", e.target.value)} />
                  </div>
                </div>
                <p className="text-xs text-purple-600 -mt-2">{t("freelance.contactNote")}</p>
                <div>
                  <Label htmlFor="fl-email">{t("freelance.email")} <span className="text-gray-400 text-xs">({t("freelance.emailHint")})</span></Label>
                  <Input id="fl-email" type="email" data-testid="freelance-email" value={form.email} onChange={(e) => set("email", e.target.value)} />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="fl-age">{t("freelance.age")} <span className="text-gray-400 text-xs">({t("freelance.optional")})</span></Label>
                    <Input id="fl-age" type="number" min={18} max={99} data-testid="freelance-age" value={form.age} onChange={(e) => set("age", e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="fl-height">{t("freelance.height")} <span className="text-gray-400 text-xs">({t("freelance.optional")})</span></Label>
                    <Input id="fl-height" data-testid="freelance-height" placeholder={t("freelance.heightPlaceholder")} value={form.height} onChange={(e) => set("height", e.target.value)} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-1">
                  <PhotoUpload which={1} preview={preview1} inputRef={file1Ref} onChange={(e) => handlePhoto(1, e)} labelText={t("freelance.photo1")} hintText={t("freelance.photoHint")} changeText={t("freelance.changePhoto")} />
                  <PhotoUpload which={2} preview={preview2} inputRef={file2Ref} onChange={(e) => handlePhoto(2, e)} labelText={t("freelance.photo2")} hintText={t("freelance.photoHint")} changeText={t("freelance.changePhoto")} />
                </div>

                <Button type="submit" disabled={submitting} data-testid="freelance-submit-btn"
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-6 text-base rounded-full">
                  {submitting ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" />{t("freelance.submitting")}</>) : t("freelance.submit")}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Link to public profiles page */}
        <div className="mt-12 text-center">
          <Link to="/profiles" data-testid="freelance-view-profiles">
            <Button variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-100 rounded-full px-8 gap-2">
              <Users className="w-4 h-4" />
              {t("freelance.viewProfiles")}
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
};

export default FreelancePage;
